import React, { useState } from "react";
import { Link } from "react-router-dom";
import { classNames } from "src/helpers/classNames";
import { ArrowPathIcon, ArrowRightIcon, CheckCircleIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { SOCIAL_MEDIA_PLATFORMS } from "src/helpers/cmsHelper";
import Button from "../form/Button";
import PlaceholderImage from "../nodata/PlaceholderImage";
import { apiRequest } from "src/async/apiUtils";
import { toast } from "react-toastify";

const MarketingPage = ({ fromAdmin = false, previewMode, site, pageContent, handleOpen = () => {} }) => {
  const siteLogo = site?.image_logo || site?.square_logo || site?.image_favicon;
  const heroData = pageContent?.hero;
  const partnersData = pageContent?.partners;
  const featuresData = pageContent?.features;
  const testimonialsData = pageContent?.testimonials;
  const calltoActionData = pageContent?.call_to_action;
  const faqData = pageContent?.faqs;
  const footerData = pageContent?.footer;
  const benefitsData = pageContent?.benefits;

  return (
    <>
      <main>
        <Header
          siteLogo={siteLogo}
          fromAdmin={fromAdmin}
        />
        <HeroSection
          edit={!previewMode}
          data={heroData}
          fromAdmin={fromAdmin}
        />

        <PartnersSection
          edit={!previewMode}
          data={partnersData}
        />

        <FeaturesSection
          edit={!previewMode}
          data={featuresData}
          handleOpen={handleOpen}
        />

        <BenifitsSection
          edit={!previewMode}
          data={benefitsData}
          handleOpen={handleOpen}
        />

        <TestimonialsSections
          edit={!previewMode}
          data={testimonialsData}
          handleOpen={handleOpen}
        />

        <CTASection
          edit={!previewMode}
          data={calltoActionData}
          handleOpen={handleOpen}
        />

        <FAQSection
          edit={!previewMode}
          data={faqData}
          handleOpen={handleOpen}
        />

        <FooterSection
          edit={!previewMode}
          data={footerData}
          handleOpen={handleOpen}
          fromAdmin={fromAdmin}
        />
      </main>
    </>
  );
};

const Header = ({ siteLogo, fromAdmin }) => {
  return (
    <>
      <header className="absolute z-50 w-full">
        <nav
          aria-label="Global"
          className="flex items-center justify-between px-6 py-8 sm:px-12">
          {siteLogo && (
            <div className="flex">
              <div className="-m-1.5 p-1.5">
                <span className="sr-only">Your Company</span>
                <img
                  alt=""
                  src={siteLogo}
                  className="h-8 w-auto sm:h-12"
                />
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <Link
              to={fromAdmin ? "#" : "/login"}
              onClick={(e) => (fromAdmin ? e.preventDefault() : "")}
              className="rounded-md border border-highlightColor p-2 px-4 font-semibold text-highlightColor transition-colors duration-300 hover:bg-highlightColor hover:text-white sm:px-6">
              Log in <span aria-hidden="true">→</span>
            </Link>
          </div>
        </nav>
      </header>
      <svg
        aria-hidden="true"
        className="absolute inset-0 -z-10 h-full w-full stroke-slate-200"
        style={{
          maskImage: "radial-gradient(100% 100% at 100% 0, #fff, #0000)",
          WebkitMaskImage: "radial-gradient(100% 100% at 100% 0, #fff, #0000)",
        }}>
        <defs>
          <pattern
            x="50%"
            y="-1"
            id="83fd4e5a-9d52-42fc-97b6-718e5d7ee527"
            width="200"
            height="200"
            patternUnits="userSpaceOnUse">
            <path
              d="M100 200V.5M.5 .5H200"
              fill="none"></path>
          </pattern>
        </defs>
        <svg
          x="50%"
          y="-1"
          className="overflow-visible fill-slate-100/50">
          <path
            d="M-100.5 0h201v201h-201Z M699.5 0h201v201h-201Z M499.5 400h201v201h-201Z M-300.5 600h201v201h-201Z"
            stroke-width="0"></path>
        </svg>
        <rect
          fill="url(#83fd4e5a-9d52-42fc-97b6-718e5d7ee527)"
          width="100%"
          height="100%"
          stroke-width="0"></rect>
      </svg>
    </>
  );
};

const HeroSection = ({ data, edit, handleOpen, fromAdmin }) => {
  return (
    <section className={classNames(data?.hidden && !edit ? "hidden" : "relative isolate")}>
      {edit && (
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <Button
            onClick={() => handleOpen("hero")}
            className="!absolute right-10 top-40 !h-10 !w-10 !rounded-lg lg:right-[45%] lg:top-48">
            <PencilSquareIcon className="h-5 w-5" />
          </Button>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:flex lg:items-center lg:gap-x-10 lg:px-8 lg:py-32">
        <div className="mx-auto max-w-2xl lg:mx-0 lg:flex-auto">
          <h1 className="mt-10 text-5xl font-semibold tracking-tight text-slate-900 [text-wrap:pretty] lg:text-7xl">{data?.title}</h1>
          <p className="mt-8 text-lg font-medium text-slate-500 [text-wrap:pretty] md:text-xl">{data?.description}</p>
          <div className="mt-10 flex items-center gap-x-6">
            <Link
              to={fromAdmin ? "#" : "/login"}
              onClick={(e) => (fromAdmin ? e.preventDefault() : "")}
              className="rounded-md bg-highlightColor px-3.5 py-2.5 text-sm font-semibold text-white hover:bg-highlightColor">
              {data?.cta_text || "Get Started"}
            </Link>
          </div>
        </div>
        <div className="mt-16 md:mt-24 lg:mt-0 lg:flex-shrink-0 lg:flex-grow">
          <svg
            role="img"
            viewBox="0 0 366 729"
            className="mx-auto w-80 max-w-full overflow-hidden">
            <title>Clearsquare</title>
            <defs>
              <clipPath id="2ade4387-9c63-4fc4-b754-10e687a0d332">
                <rect
                  rx="36"
                  width="316"
                  height="684"></rect>
              </clipPath>
            </defs>
            <path
              d="M363.315 64.213C363.315 22.99 341.312 1 300.092 1H66.751C25.53 1 3.528 22.99 3.528 64.213v44.68l-.857.143A2 2 0 0 0 1 111.009v24.611a2 2 0 0 0 1.671 1.973l.95.158a2.26 2.26 0 0 1-.093.236v26.173c.212.1.398.296.541.643l-1.398.233A2 2 0 0 0 1 167.009v47.611a2 2 0 0 0 1.671 1.973l1.368.228c-.139.319-.314.533-.511.653v16.637c.221.104.414.313.56.689l-1.417.236A2 2 0 0 0 1 237.009v47.611a2 2 0 0 0 1.671 1.973l1.347.225c-.135.294-.302.493-.49.607v377.681c0 41.213 22 63.208 63.223 63.208h95.074c.947-.504 2.717-.843 4.745-.843l.141.001h.194l.086-.001 33.704.005c1.849.043 3.442.37 4.323.838h95.074c41.222 0 63.223-21.999 63.223-63.212v-394.63c-.259-.275-.48-.796-.63-1.47l-.011-.133 1.655-.276A2 2 0 0 0 366 266.62v-77.611a2 2 0 0 0-1.671-1.973l-1.712-.285c.148-.839.396-1.491.698-1.811V64.213Z"
              fill="#4B5563"></path>
            <path
              d="M16 59c0-23.748 19.252-43 43-43h246c23.748 0 43 19.252 43 43v615c0 23.196-18.804 42-42 42H58c-23.196 0-42-18.804-42-42V59Z"
              fill="#343E4E"></path>
            <foreignObject
              width="316"
              height="684"
              clip-path="url(#2ade4387-9c63-4fc4-b754-10e687a0d332)"
              transform="translate(24 24)"
              className="overflow-hidden">
              {data?.image ? (
                <img
                  alt=""
                  src={data?.image}
                  className="h-full w-full object-cover"
                />
              ) : (
                <PlaceholderImage
                  textClasses="hidden"
                  containerClasses="opacity-80"
                />
              )}
            </foreignObject>
          </svg>
        </div>
      </div>
    </section>
  );
};

const PartnersSection = ({ edit, data, handleOpen }) => {
  return (
    <section className={classNames(data?.hidden && !edit ? "hidden" : "relative mx-auto max-w-7xl px-6 py-16 lg:px-8")}>
      {edit && (
        <Button
          onClick={() => handleOpen("partners")}
          className="!absolute -top-20 right-10 mt-1 !h-10 !w-10 !ring-0 ">
          <PencilSquareIcon className="h-5 w-5" />
        </Button>
      )}
      <div
        className="mx-auto grid grid-cols-2 items-center gap-x-6 gap-y-8 
             sm:grid-cols-3 sm:gap-x-4 sm:gap-y-6 
             md:grid-cols-5 md:gap-x-8 md:gap-y-10 
             lg:max-w-5xl lg:grid-cols-5 lg:gap-x-12 lg:gap-y-14">
        {data?.logos?.length > 0
          ? data.logos.map((item, index) => (
              <img
                key={index}
                alt={item?.name || "Logo"}
                src={item?.logo}
                width="158"
                height="48"
                className="col-span-1 max-h-12 w-full object-contain transition-transform duration-200 hover:scale-105"
              />
            ))
          : [1, 2, 3, 4, 5].map((_, index) => (
              <PlaceholderImage
                key={index}
                containerClasses="w-[100px] h-[70px] !flex-row gap-x-5 !rounded-xl !border-none !bg-transparent !opacity-60"
                iconClasses="!h-10 !w-10"
              />
            ))}
      </div>
    </section>
  );
};

const FeaturesSection = ({ data, edit, handleOpen }) => {
  return (
    <section className={classNames(data?.hidden && !edit ? "hidden" : "animate-fade-in relative mx-auto mt-20 max-w-7xl sm:px-8 md:mt-36 md:px-6")}>
      {edit && (
        <Button
          onClick={() => handleOpen("features")}
          className="!absolute -top-20 right-10 mt-1 !h-10 !w-10 !rounded-md !ring-0 ">
          <PencilSquareIcon className="h-5 w-5" />
        </Button>
      )}
      <div className="relative isolate overflow-hidden bg-slate-900 px-6 py-20 md:rounded-3xl md:px-10 md:py-24 lg:px-32 lg:py-24">
        <div className="mx-auto grid max-w-2xl grid-cols-1 items-center gap-x-8 gap-y-16 md:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:gap-y-0">
          <div className="lg:row-start-2 lg:max-w-md">
            <h2 className="text-3xl font-semibold tracking-tight text-white [text-wrap:balance] md:text-4xl">{data?.title}</h2>
            <p className="mt-6 text-lg text-slate-300">{data?.description}</p>
          </div>
          <div className="relative -z-20 min-w-full max-w-2xl overflow-hidden rounded-xl shadow-xl ring-1 ring-slate-100/10 lg:row-span-4 lg:w-[64rem] lg:max-w-none">
            {data?.image ? (
              <img
                alt="Product screenshot"
                src={data?.image}
                width="2432"
                height="1442"
                className=""
              />
            ) : (
              <PlaceholderImage
                containerClasses="!opacity-20"
                textClasses="text-2xl !hidden"
              />
            )}
          </div>
          {data?.features?.length > 0 && (
            <div className="max-w-2xl lg:row-start-3 lg:mt-10 lg:max-w-md lg:border-t lg:border-slate-100/10 lg:pt-10">
              <dl className="max-w-2xl space-y-8 text-base text-slate-300 lg:max-w-none">
                {data?.features?.map((item, index) => (
                  <div
                    className="relative"
                    key={index}>
                    <dt className="ml-9 inline-block font-semibold text-white">
                      <span
                        className="absolute left-1 top-0.5 h-5 w-5 text-highlightColor"
                        dangerouslySetInnerHTML={{ __html: item?.icon }}
                      />
                      {item?.title}
                    </dt>
                    <dd className="ml-2 inline">{item?.description}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-12 top-1/2 -z-10 -translate-y-1/2 transform-gpu blur-3xl lg:-bottom-48 lg:top-auto lg:translate-y-0">
          <div className="aspect-[1155/678] w-[72rem] bg-gradient-to-tr from-pink-400 to-violet-400 opacity-25 [clip-path:polygon(74.1%_44.1%,100%_61.6%,97.5%_26.9%,85.5%_0.1%,80.7%_2%,72.5%_32.5%,60.2%_62.4%,52.4%_68.1%,47.5%_58.3%,45.2%_34.5%,27.5%_76.7%,0.1%_64.9%,17.9%_100%,27.6%_76.8%,76.1%_97.7%,74.1%_44.1%)]" />
        </div>
      </div>
    </section>
  );
};

const BenifitsSection = ({ data, edit, handleOpen }) => {
  return (
    <section className={classNames(data?.hidden && !edit ? "hidden" : "relative mx-auto mt-32 max-w-7xl px-6 sm:mt-56 lg:px-8")}>
      {edit && (
        <Button
          onClick={() => handleOpen("benefits")}
          className="!absolute -top-20 right-10 mt-1 !h-10 !w-10 !rounded-md !ring-0 ">
          <PencilSquareIcon className="h-5 w-5" />
        </Button>
      )}
      <div className="mx-auto max-w-2xl lg:text-center">
        <p className="mt-2 text-4xl font-semibold tracking-tight text-slate-900 [text-wrap:pretty] sm:text-5xl lg:[text-wrap:balance]">{data?.title}</p>
        <p className="mt-6 text-lg text-slate-500">{data?.description}</p>
      </div>
      {data?.benefits?.length > 0 && (
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
            {data?.benefits?.map((item, index) => (
              <div
                className="flex flex-col"
                key={index}>
                <dt className="flex items-center gap-x-3 text-base font-semibold text-slate-800">
                  <span
                    dangerouslySetInnerHTML={{ __html: item?.icon }}
                    className="h-5 w-5 flex-none text-highlightColor"
                  />
                  {item?.title}
                </dt>
                <dd className="mt-4 flex flex-auto flex-col text-base text-slate-500">
                  <div className="flex-auto">{item?.description}</div>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </section>
  );
};

const TestimonialsSections = ({ data, edit, handleOpen }) => {
  return (
    <section className={classNames(data?.hidden && !edit ? "hidden" : "relative isolate mt-16 overflow-hidden px-6 lg:mt-24 lg:px-8")}>
      {edit && (
        <Button
          onClick={() => handleOpen("testimonials")}
          className="!absolute right-10 z-50 mt-1 !h-10 !w-10 !rounded-md !ring-0 ">
          <PencilSquareIcon className="h-5 w-5" />
        </Button>
      )}
      <div className="relative mx-auto max-w-2xl py-24 sm:py-32 lg:max-w-4xl">
        <div className="bg-gradient-radial absolute left-1/2 top-0 -z-10 h-[50rem] w-[90rem] -translate-x-1/2 from-indigo-100 to-white opacity-20 lg:left-36"></div>
        <div className="absolute inset-y-0 right-1/2 -z-10 mr-12 w-[150vw] origin-bottom-left skew-x-[-30deg] bg-white shadow-xl ring-indigo-700/50 sm:mr-20 md:mr-0 lg:right-full lg:-mr-36 lg:origin-center"></div>
        <figure className="grid grid-cols-1 items-center gap-x-6 gap-y-8 lg:gap-x-10">
          <div className="relative col-span-2 lg:col-start-1 lg:row-start-2">
            <svg
              fill="none"
              viewBox="0 0 162 128"
              aria-hidden="true"
              className="absolute -top-12 left-0 -z-10 h-32 stroke-slate-900/10">
              <path
                d="M65.5697 118.507L65.8918 118.89C68.9503 116.314 71.367 113.253 73.1386 109.71C74.9162 106.155 75.8027 102.28 75.8027 98.0919C75.8027 94.237 75.16 90.6155 73.8708 87.2314C72.5851 83.8565 70.8137 80.9533 68.553 78.5292C66.4529 76.1079 63.9476 74.2482 61.0407 72.9536C58.2795 71.4949 55.276 70.767 52.0386 70.767C48.9935 70.767 46.4686 71.1668 44.4872 71.9924L44.4799 71.9955L44.4726 71.9988C42.7101 72.7999 41.1035 73.6831 39.6544 74.6492C38.2407 75.5916 36.8279 76.455 35.4159 77.2394L35.4047 77.2457L35.3938 77.2525C34.2318 77.9787 32.6713 78.3634 30.6736 78.3634C29.0405 78.3634 27.5131 77.2868 26.1274 74.8257C24.7483 72.2185 24.0519 69.2166 24.0519 65.8071C24.0519 60.0311 25.3782 54.4081 28.0373 48.9335C30.703 43.4454 34.3114 38.345 38.8667 33.6325C43.5812 28.761 49.0045 24.5159 55.1389 20.8979C60.1667 18.0071 65.4966 15.6179 71.1291 13.7305C73.8626 12.8145 75.8027 10.2968 75.8027 7.38572C75.8027 3.6497 72.6341 0.62247 68.8814 1.1527C61.1635 2.2432 53.7398 4.41426 46.6119 7.66522C37.5369 11.6459 29.5729 17.0612 22.7236 23.9105C16.0322 30.6019 10.618 38.4859 6.47981 47.558L6.47976 47.558L6.47682 47.5647C2.4901 56.6544 0.5 66.6148 0.5 77.4391C0.5 84.2996 1.61702 90.7679 3.85425 96.8404L3.8558 96.8445C6.08991 102.749 9.12394 108.02 12.959 112.654L12.959 112.654L12.9646 112.661C16.8027 117.138 21.2829 120.739 26.4034 123.459L26.4033 123.459L26.4144 123.465C31.5505 126.033 37.0873 127.316 43.0178 127.316C47.5035 127.316 51.6783 126.595 55.5376 125.148L55.5376 125.148L55.5477 125.144C59.5516 123.542 63.0052 121.456 65.9019 118.881L65.5697 118.507Z"
                id="b56e9dab-6ccb-4d32-ad02-6b4bb5d9bbeb"></path>
              <use
                x="86"
                href="#b56e9dab-6ccb-4d32-ad02-6b4bb5d9bbeb"></use>
            </svg>
            <blockquote className="text-xl font-semibold text-highlightColor sm:text-2xl">
              <p>{data?.comment}</p>
            </blockquote>
          </div>
          <div className="col-end-1 mb-auto aspect-square w-20 lg:row-span-4 lg:w-72">
            <div className="h-full w-full overflow-hidden rounded-xl bg-indigo-50 lg:rounded-3xl">
              {data?.image ? (
                <img
                  alt=""
                  src={data?.image}
                  className="h-full w-full object-cover"
                />
              ) : (
                <PlaceholderImage
                  containerClasses="!bg-indigo-200 opacity-80"
                  textClasses="hidden"
                />
              )}
            </div>
          </div>
          <figcaption className="text-base leading-snug lg:col-start-1 lg:row-start-3">
            <div className="font-semibold text-highlightColor">{data?.name}</div>
            <div className="mt-1 text-slate-500">{data?.role}</div>
          </figcaption>
        </figure>
      </div>
    </section>
  );
};

const CTASection = ({ edit, data, handleOpen }) => {
  return (
    <section className={classNames(data?.hidden && !edit ? "hidden" : "relative mx-auto mt-32 max-w-7xl sm:mt-56 sm:px-6 lg:px-8")}>
      {edit && (
        <Button
          onClick={() => handleOpen("call_to_action")}
          className="!absolute -top-24  right-10 mt-1 !h-10 !w-10 !rounded-md !ring-0 ">
          <PencilSquareIcon className="h-5 w-5" />
        </Button>
      )}
      <div className="relative isolate overflow-hidden bg-slate-900 px-6 py-24 shadow-2xl sm:rounded-3xl sm:px-24 xl:py-32">
        <h2 className="mx-auto max-w-3xl text-center text-4xl font-semibold leading-tight tracking-tighter text-white md:text-5xl md:leading-none">{data?.title}</h2>
        <p className="mx-auto mt-6 max-w-xl text-center text-lg leading-normal text-slate-300">{data?.description}</p>
        <div className="mt-16 flex justify-center">
          <Link
            to={data?.button_link}
            target="_blank"
            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-base font-semibold text-highlightColor shadow-lg transition-all hover:bg-highlightColor hover:text-white focus:outline-none focus:ring-2 focus:ring-highlightColor focus:ring-offset-2">
            <span>{data?.button_text}</span>
            <ArrowRightIcon className="h-5 w-5" />
          </Link>
        </div>

        <svg
          viewBox="0 0 1024 1024"
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 -z-10 h-[64rem] w-[64rem] -translate-x-1/2">
          <circle
            r="512"
            cx="512"
            cy="512"
            fill="url(#759c1415-0410-454c-8f7c-9a820de03641)"
            fill-opacity="0.7"></circle>
          <defs>
            <radialGradient
              r="1"
              cx="0"
              cy="0"
              id="759c1415-0410-454c-8f7c-9a820de03641"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(512 512) rotate(90) scale(512)">
              <stop stop-color="#7775D6"></stop>
              <stop
                offset="1"
                stop-color="#E935C1"
                stop-opacity="0"></stop>
            </radialGradient>
          </defs>
        </svg>
      </div>
    </section>
  );
};

const FAQSection = ({ data, edit, handleOpen }) => {
  const FaqItem = ({ data }) => {
    const [isFaqOpen, setFaqOpen] = useState(false);
    return (
      <>
        <div className="py-6 first:pt-0 last:pb-0">
          <dt>
            <button
              className="flex w-full items-start justify-between text-left text-slate-900"
              type="button"
              onClick={() => setFaqOpen(!isFaqOpen)}>
              <span className="text-lg font-semibold">{data?.question}</span>
              <span className="ml-6 flex h-7 items-center">
                {isFaqOpen ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    data-slot="icon"
                    className="h-8 w-8">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M18 12H6"></path>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    data-slot="icon"
                    className="h-8 w-8">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M12 6v12m6-6H6"></path>
                  </svg>
                )}
              </span>
            </button>
          </dt>
          {isFaqOpen && (
            <dd className="mt-2 pr-12">
              <div className="text-base text-slate-500">{data?.answer}</div>
            </dd>
          )}
        </div>
      </>
    );
  };

  return (
    <section className={classNames(data?.hidden && !edit ? "hidden" : "relative mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 lg:py-40")}>
      {edit && (
        <Button
          onClick={() => handleOpen("faqs")}
          className="!absolute  right-10 top-32 mt-1 !h-10 !w-10 !rounded-md !ring-0 ">
          <PencilSquareIcon className="h-5 w-5" />
        </Button>
      )}
      <div className="mx-auto max-w-4xl">
        <h2 className="text-4xl font-semibold tracking-tight text-blue-950 sm:text-5xl sm:leading-tight">{data?.title}</h2>
        {data?.faqs?.length > 0 && (
          <dl className="mt-16 divide-y divide-gray-900/10">
            {data?.faqs?.map((item, index) => (
              <FaqItem
                key={index}
                data={item}
              />
            ))}
          </dl>
        )}
      </div>
    </section>
  );
};

const FooterSection = ({ edit, site, data, handleOpen, fromAdmin }) => {
  const [email, setEmail] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [success, setSucsess] = useState(false);

  const handleSubscribeNewsLetter = async () => {
    try {
      setDisabled(true);
      await apiRequest("post", `/portal/newsletter/subscribers`, { body: { email } });
      setSucsess(true);
      setTimeout(() => {
        setEmail("");
        setSucsess(false);
      }, 1000);
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setDisabled(false);
    }
  };

  return (
    <footer className={classNames(data?.hidden && !edit ? "hidden" : "relative bg-slate-900")}>
      {edit && (
        <Button
          onClick={() => handleOpen("footer")}
          className="!absolute -top-20 right-10 !h-10  !w-10 !rounded-lg !ring-0">
          <PencilSquareIcon className="h-5 w-5" />
        </Button>
      )}
      <div className="mx-auto max-w-6xl px-6 pb-8 pt-16 md:pt-24 lg:px-8 lg:pt-32">
        <div className="lg:flex lg:items-center lg:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-white">{data?.title}</h3>
            <p className="mt-2 text-sm text-slate-300">{data?.description}</p>
          </div>
          <form className="mt-6 flex gap-2 md:max-w-xl lg:mt-0">
            <label
              for="email-address"
              className="sr-only">
              Email address
            </label>
            <input
              id="email-address"
              name="email-address"
              type="email"
              required=""
              value={email}
              placeholder="Enter your email"
              autocomplete="email"
              onChange={(e) => setEmail(e.target.value)}
              className="min-w-0 flex-auto rounded-md border border-slate-300/10 bg-white/5 px-3.5 py-2 text-base leading-normal text-white !ring-0 focus:border-white md:text-sm"
            />
            <div className="flex-shrink-0">
              <button
                type="submit"
                disabled={disabled || success || fromAdmin}
                onClick={handleSubscribeNewsLetter}
                className="flex gap-2 rounded-md bg-highlightColor px-3.5 py-4 text-sm font-semibold leading-tight text-white shadow-sm hover:bg-highlightColor">
                {data?.button_text}
                {disabled ? <ArrowPathIcon className="h-4 w-4 animate-spin stroke-2" /> : success ? <CheckCircleIcon className="h-4 w-4 stroke-2" /> : null}
              </button>
            </div>
          </form>
        </div>
        <div className="mt-8 border-t border-solid border-slate-800 pt-8 md:flex md:items-center md:justify-between">
          <div className="flex justify-center gap-x-6 sm:justify-start md:order-2">
            {data?.social_links &&
              Object.keys(data.social_links).length > 0 &&
              Object.entries(data.social_links).map(([key, href]) => {
                if (!href || !href.trim()) return null;

                const platform = SOCIAL_MEDIA_PLATFORMS.find((p) => p.key === key);
                if (!platform) return null;

                return (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 transition-colors duration-200 hover:text-white"
                    aria-label={platform.label}>
                    {platform.icon}
                  </a>
                );
              })}
          </div>
          <p className="mt-8 text-center text-sm leading-6 text-slate-400 sm:text-left md:order-1 md:mt-0">
            &copy; {new Date().getFullYear()} {site?.site_name}, Inc. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default MarketingPage;
